import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { boosterData } from "../services/BoosterServices";
import { useSearchParams } from "react-router-dom";

export interface boosterQuantity {
  booster: boosterData;
  quantity: number;
  maxQuantity: number;
}

interface CartContextType {
  items: boosterQuantity[];
  addToCart: (item: boosterQuantity) => void;
  removeFromCart: (booster: boosterData) => void;
  increaseItemQuantity: (item: boosterQuantity) => void;
  decreaseItemQuantity: (item: boosterQuantity) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<boosterQuantity[]>(() => {
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkoutSuccess = searchParams.get("checkout_success");
    const isTypeDigital = searchParams.get("type") === "digital";
    if (checkoutSuccess && isTypeDigital) {
      setItems([]);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  const addToCart = (item: boosterQuantity) => {
    setItems((prev) => [...prev, item]);
  };

  const removeFromCart = (booster: boosterData) => {
    setItems(items.filter((item) => item.booster !== booster));
  };

  const increaseItemQuantity = (item: boosterQuantity) => {
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.booster.id === item.booster.id
          ? { ...i, quantity: Math.min(i.quantity + 1, i.maxQuantity) } // Mise à jour immuable
          : i
      )
    );
  };

  const decreaseItemQuantity = (item: boosterQuantity) => {
    setItems((prevItems) =>
      prevItems.map((i) =>
        i.booster.id === item.booster.id
          ? { ...i, quantity: Math.max(i.quantity - 1, 1) } // Mise à jour immuable
          : i
      )
    );
  };

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        increaseItemQuantity,
        decreaseItemQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
